import { Alert, AlertDescription, AlertIcon, Text } from "@chakra-ui/react";
import { FC, useEffect, useMemo, useState } from "react";
import { graphql, useFragment } from "react-relay";

import { UrlMappingCacheStatus_user$key } from "~/src/__relay_artifacts__/UrlMappingCacheStatus_user.graphql";

const userFragment = graphql`
  fragment UrlMappingCacheStatus_user on User {
    urlMappingCache {
      present
      expiresAt
      remainingSeconds
      ttlSeconds
    }
  }
`;

type Props = {
  userRef: UrlMappingCacheStatus_user$key;
};

const formatRemaining = (seconds: number): string => {
  const safe = Math.max(0, seconds);
  const m = Math.floor(safe / 60);
  const s = safe % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
};

export const UrlMappingCacheStatus: FC<Props> = ({ userRef }) => {
  const data = useFragment(userFragment, userRef);
  const cache = data.urlMappingCache;
  const expiresAtMs = useMemo(() => {
    if (!cache?.expiresAt) return null;
    const t = Date.parse(cache.expiresAt);
    return Number.isNaN(t) ? null : t;
  }, [cache?.expiresAt]);

  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!cache?.present || expiresAtMs == null) return undefined;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [cache?.present, expiresAtMs]);

  const remainingSeconds =
    expiresAtMs != null
      ? Math.max(0, Math.ceil((expiresAtMs - now) / 1000))
      : cache?.remainingSeconds ?? null;

  const ttlMinutes = Math.round((cache?.ttlSeconds ?? 300) / 60);

  return (
    <Alert status="info" variant="left-accent" mb="16px" borderRadius="md">
      <AlertIcon />
      <AlertDescription fontSize="sm">
        <Text as="span" fontWeight="bold">
          ルールキャッシュ：
        </Text>{" "}
        {cache?.present && remainingSeconds != null ? (
          <>
            次回更新まで{" "}
            <Text as="span" fontWeight="bold" fontFamily="mono">
              {formatRemaining(remainingSeconds)}
            </Text>
            （最大 {ttlMinutes} 分）
          </>
        ) : (
          <>なし（サイトへのアクセス後に生成・最大 {ttlMinutes} 分）</>
        )}
        <Text fontSize="xs" color="gray.600" mt="4px">
          ルールの保存・有効切替はすぐに反映されます。開始/終了日時は毎リクエストで判定されます。
        </Text>
      </AlertDescription>
    </Alert>
  );
};
